import { useLayoutEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { scrollToSection } from "./useScrollToSection.js";

/**
 * Where the viewport lands after a route change.
 *
 * Three cases, in order:
 *
 *  1. `state.scrollToId` — a link that meant "go to Home and show me #pricing".
 *     It replaced `navigate("/"); setTimeout(scrollTo, 100)`, which was written
 *     in three places and guessed at how long the destination would take to
 *     mount. The guess was wrong in both directions: on the homepage the scroll
 *     fired while a black loading overlay still had ~700ms to run, and on a slow
 *     connection it fired before the sections existed at all. State carries the
 *     intent to the other side of the navigation instead of racing it.
 *
 *     The state is then cleared with a `replace`, so a reload or a Back does not
 *     re-scroll a page the visitor has since scrolled themselves.
 *
 *  2. `location.hash` — an ordinary fragment link.
 *  3. otherwise, the top.
 *
 * Ordering is correct by construction: this lives in `App`, the parent, and
 * child layout effects run first — so the page's `usePageShell` has already
 * initialised the interaction engine and revealed the body before anything
 * here measures a position.
 */
/**
 * Scroll to a section once its position has stopped moving.
 *
 * A freshly mounted route is still settling when the layout effect runs: the
 * interaction engine stamps transforms, the hero image and the web font arrive,
 * and every one of those changes how far down the page a section actually sits.
 * Measuring in the first frame produces a number that is already stale — the
 * observed failure was a hand-off to `#pricing` landing 62px down a page whose
 * pricing section starts several thousand pixels in.
 *
 * So measure until the answer repeats, then scroll. Bounded at 60 frames (~1s)
 * so a page that never settles still scrolls somewhere sensible rather than
 * never scrolling at all. This is the deterministic replacement for the
 * `setTimeout(scrollTo, 100)` that three call sites used to guess with.
 */
const scrollWhenSettled = (targetId) => {
  let previousTop = null;
  let frames = 0;

  const measure = () => {
    const element = document.getElementById(targetId);
    if (!element) {
      if (frames++ < 60) requestAnimationFrame(measure);
      return;
    }
    const top = Math.round(element.getBoundingClientRect().top + window.scrollY);
    if (top === previousTop || frames++ >= 60) {
      scrollToSection(targetId);
      return;
    }
    previousTop = top;
    requestAnimationFrame(measure);
  };

  requestAnimationFrame(measure);
};

export default function useRouteScrollBehaviour() {
  const location = useLocation();
  const navigate = useNavigate();
  const clearedFor = useRef(null);

  useLayoutEffect(() => {
    const scrollToId = location.state?.scrollToId;

    if (scrollToId) {
      clearedFor.current = location.key;
      scrollWhenSettled(scrollToId);
      navigate(location.pathname + location.search, {
        replace: true,
        state: null,
      });
      return;
    }

    // The `replace` above re-runs this effect a frame later with the state
    // gone — our own navigation, not a new one. Falling through to the
    // scroll-to-top below would cancel the scroll just started, which is
    // exactly what it did: the hand-off to #pricing landed at the top of the
    // page instead, intermittently, depending on which won the race.
    if (clearedFor.current !== null) {
      clearedFor.current = null;
      return;
    }

    if (location.hash) {
      scrollWhenSettled(location.hash.slice(1));
      return;
    }

    window.scrollTo(0, 0);
  }, [location, navigate]);
}
